import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios'

import { refreshTokenFx } from '../model'
import { axiosConfigSet, getAxiosConfigFx } from '../model/axios.model'
import { apiRequestError, errorHandlerFx } from '../model/error.model'

export type ResponseConfig<TData = unknown> = {
  data: TData
  status: number
  statusText: string
  headers: AxiosResponse['headers']
}

export type ResponseErrorConfig<TError = unknown> = AxiosError<TError>

export const axiosInstance = axios.create()

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean
    }

    if (
      [401, 403].includes(error.response?.status || 0) &&
      !originalRequest._retry
    ) {
      if (!refreshTokenFx.pending.getState()) {
        void refreshTokenFx()
      }
      try {
        const newToken = await new Promise<string>((resolve, reject) => {
          const unsubDone = refreshTokenFx.doneData.watch((token) => {
            unsubDone()
            unsubFail()
            resolve(token)
          })
          const unsubFail = refreshTokenFx.failData.watch((err) => {
            unsubDone()
            unsubFail()
            reject(err)
          })
        })
        originalRequest._retry = true
        const headers =
          originalRequest.headers &&
          typeof originalRequest.headers.toJSON === 'function'
            ? (originalRequest.headers.toJSON() as Record<string, string>)
            : (originalRequest.headers as Record<string, string> | undefined)

        originalRequest.headers = {
          ...headers,
          Authorization: `Bearer ${newToken}`,
        }
        return await axiosInstance(originalRequest)
      } catch (refreshErr) {
        if (refreshErr instanceof Error) {
          return Promise.reject(refreshErr)
        }
        return Promise.reject(new Error(String(refreshErr)))
      }
    }

    return Promise.reject(error)
  },
)

export const client = async <TData = unknown, TVariables = unknown>(
  config: AxiosRequestConfig<TVariables>,
): Promise<ResponseConfig<TData>> => {
  const globalConfig = await getAxiosConfigFx()
  try {
    return await axiosInstance.request<TData, ResponseConfig<TData>>({
      ...globalConfig,
      ...config,
      headers: Object.assign({}, globalConfig.headers, config.headers),
    })
  } catch (err) {
    try {
      await errorHandlerFx(err as AxiosError)
    } catch (handledError) {
      apiRequestError(handledError as Error)
      throw handledError
    }
    throw err
  }
}

client.getConfig = getAxiosConfigFx
client.setConfig = axiosConfigSet

export default client
